import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * Combobox reutilizable con búsqueda por teclado.
 *
 * Props:
 * - value: string   → id seleccionado ("" = ninguno)
 * - onChange: (id: string) => void
 * - options: { value: string, label: string, hint?: string }[]
 * - placeholder: string
 * - allowClear: bool (default true)
 * - "data-testid": string
 * - disabled: bool
 * - className: string extra para el trigger
 */
export default function SearchableSelect({
    value,
    onChange,
    options,
    placeholder = "Buscar...",
    allowClear = true,
    disabled = false,
    className = "",
    ...rest
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    const selected = useMemo(
        () => options.find((o) => o.value === value) || null,
        [options, value],
    );

    const filtered = useMemo(() => {
        if (!query.trim()) return options;
        const q = query.trim().toLowerCase();
        return options.filter((o) => {
            const lab = String(o.label || "").toLowerCase();
            const hint = String(o.hint || "").toLowerCase();
            return lab.includes(q) || hint.includes(q);
        });
    }, [options, query]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 10);
        else setQuery("");
    }, [open]);

    const pick = (v) => {
        onChange(v);
        setOpen(false);
    };

    const clear = (e) => {
        e.stopPropagation();
        onChange("");
    };

    return (
        <div ref={wrapRef} className="relative" data-testid={rest["data-testid"]}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                className={`w-full flex items-center justify-between border border-slate-300 rounded-sm px-3 py-2 bg-white text-sm text-left hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
                data-testid={
                    rest["data-testid"]
                        ? `${rest["data-testid"]}-trigger`
                        : "combobox-trigger"
                }
            >
                <span
                    className={
                        selected ? "truncate text-slate-900" : "text-slate-400"
                    }
                >
                    {selected ? selected.label : placeholder}
                </span>
                <span className="flex items-center gap-1 flex-shrink-0 ml-2">
                    {allowClear && selected && !disabled && (
                        <span
                            role="button"
                            tabIndex={-1}
                            onClick={clear}
                            className="text-slate-400 hover:text-slate-600 p-0.5"
                            data-testid={
                                rest["data-testid"]
                                    ? `${rest["data-testid"]}-clear`
                                    : "combobox-clear"
                            }
                        >
                            <X size={12} />
                        </span>
                    )}
                    <CaretDown size={12} className="text-slate-400" />
                </span>
            </button>
            {open && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-hidden flex flex-col">
                    <div className="relative border-b border-slate-100">
                        <MagnifyingGlass
                            size={12}
                            className="absolute left-2 top-2.5 text-slate-400"
                        />
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Escribe para buscar..."
                            className="w-full pl-7 pr-2 py-2 text-sm outline-none"
                            data-testid={
                                rest["data-testid"]
                                    ? `${rest["data-testid"]}-search`
                                    : "combobox-search"
                            }
                        />
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {filtered.length === 0 ? (
                            <div className="text-center py-6 text-xs text-slate-400">
                                Sin resultados
                            </div>
                        ) : (
                            filtered.slice(0, 200).map((o) => (
                                <button
                                    type="button"
                                    key={o.value}
                                    onClick={() => pick(o.value)}
                                    className={`w-full flex items-center justify-between text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                                        o.value === value
                                            ? "bg-[#002BAF]/5 text-[#002BAF] font-medium"
                                            : "text-slate-700"
                                    }`}
                                    data-testid={
                                        rest["data-testid"]
                                            ? `${rest["data-testid"]}-option-${o.value}`
                                            : undefined
                                    }
                                >
                                    <span className="truncate">{o.label}</span>
                                    {o.hint && (
                                        <span className="text-[10px] text-slate-400 font-mono ml-2 flex-shrink-0">
                                            {o.hint}
                                        </span>
                                    )}
                                </button>
                            ))
                        )}
                        {filtered.length > 200 && (
                            <div className="text-center py-2 text-[10px] text-slate-400 border-t border-slate-100">
                                Mostrando 200 de {filtered.length}. Refina la búsqueda.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
